import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Select } from 'blaze-ui/Select/Select'
import { Text } from 'blaze-ui/Text/Text'
import { Item } from '@react-stately/collections'
import Styles from './WordPressSettingsModal.module.scss'
import { useState } from 'react'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { WordPressIntegration, WordPressProfile } from 'api/clients/ExternalIntegrationsClient'

interface WordPressSettingsModalProps {
  integration: WordPressIntegration
}

export const WordPressSettingsModal = ({ integration }: WordPressSettingsModalProps) => {
  const { closeModal } = useModals()
  const updateExternalIntegration = useUpdateExternalIntegration()

  const wpProfile = integration.additional_data as WordPressProfile | undefined
  const wordpressUsers = wpProfile?.wordpress_users ?? []

  // Compute initial author ID to avoid controlled/uncontrolled component warning
  const getInitialAuthorId = (): string | undefined => {
    if (wordpressUsers.length === 0) return undefined

    // Check if there's a previously selected author stored
    if (wpProfile?.selected_author_id) {
      const existingAuthor = wordpressUsers.find((user) => user.id === wpProfile.selected_author_id)
      if (existingAuthor) {
        return String(existingAuthor.id)
      }
    }

    // Default to first user
    return String(wordpressUsers[0].id)
  }

  const [selectedAuthorId, setSelectedAuthorId] = useState<string | undefined>(getInitialAuthorId)

  const handleAuthorChange = (authorId: string) => {
    setSelectedAuthorId(authorId)
  }

  const handleConfirm = async () => {
    if (!selectedAuthorId) {
      showToastMessage('Please select a default author', 'error')
      return
    }

    try {
      await updateExternalIntegration.mutateAsync({
        id: integration.id,
        additional_data: {
          selected_author_id: Number(selectedAuthorId),
        } as Partial<WordPressProfile>,
      })

      showToastMessage('WordPress settings saved successfully!', 'success')
      closeModal()
    } catch (error) {
      showToastMessage('Failed to save WordPress settings. Please try again.', 'error')
    }
  }

  const hasUsers = wordpressUsers.length > 0

  return (
    <Modal.Root size="sm" aria-label="Configure WordPress Integration">
      <Modal.Header title="WordPress Integration Settings" />
      <Modal.Content>
        <div className={Styles.content}>
          <Heading level={4}>Default author for all blog posts</Heading>
          {hasUsers ? (
            <Select
              aria-label="Select default author"
              placeholder="Select an author"
              selectedKey={selectedAuthorId}
              onSelectionChange={(key) => handleAuthorChange(key as string)}
              useButtonWidth={true}
              useFullWidth={true}
              variant="secondary"
              size="lg"
            >
              {wordpressUsers.map((user) => (
                <Item key={String(user.id)} textValue={user.name}>
                  {user.name}
                </Item>
              ))}
            </Select>
          ) : (
            <Text variant="secondary" color="var(--dark-60)">
              No WordPress users found. Users will be available once they are synced from your WordPress site.
            </Text>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleConfirm}
            isDisabled={!selectedAuthorId || !hasUsers || updateExternalIntegration.isLoading}
          >
            Save Changes
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
